import { Controller } from "@hotwired/stimulus"

// Drives an add-on product card through its three states, keeping a hidden form
// input in sync and dispatching a change so the order summary reloads:
//
//   1. Optional item at quantity 0 shows a single "Add" button.
//   2. Once added (or for required items) it shows a [-] [ n ] [+] stepper.
//   3. The minus button disables at the minimum quantity (1 for required
//      items, so they can't be removed); the plus button disables — and a
//      "max limit reached" notice appears — once maxValue is hit.
export default class extends Controller {
  static targets = [
    "addButton",
    "decrement",
    "display",
    "increment",
    "input",
    "maxNotice",
    "stepper",
  ]
  static values = { max: Number, min: Number }

  connect() {
    this.render()
  }

  add() {
    this.setQuantity(this.minValue + 1)
  }

  decrement() {
    this.setQuantity(this.quantity - 1)
  }

  increment() {
    this.setQuantity(this.quantity + 1)
  }

  get quantity() {
    return parseInt(this.inputTarget.value, 10) || 0
  }

  get atMax() {
    return this.maxValue > 0 && this.quantity >= this.maxValue
  }

  setQuantity(value) {
    let next = value

    if (next < this.minValue) next = this.minValue
    if (this.maxValue > 0 && next > this.maxValue) next = this.maxValue

    if (next === this.quantity) return

    this.inputTarget.value = next
    this.render()
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  render() {
    if (this.hasDisplayTarget) this.displayTarget.textContent = this.quantity

    // Optional items collapse back to the "Add" button at quantity 0.
    if (this.hasAddButtonTarget) {
      const collapsed = this.quantity === 0
      this.addButtonTarget.classList.toggle("hidden", !collapsed)
      if (this.hasStepperTarget) this.stepperTarget.classList.toggle("hidden", collapsed)
    }

    if (this.hasDecrementTarget) this.decrementTarget.disabled = this.quantity <= this.minValue
    if (this.hasIncrementTarget) this.incrementTarget.disabled = this.atMax
    if (this.hasMaxNoticeTarget) this.maxNoticeTarget.classList.toggle("hidden", !this.atMax)
  }
}
